import React, { useState, useEffect } from "react";
import {
  Search,
  Bell,
  Calendar,
  Video,
  RotateCcw,
  XCircle,
  Clock,
  User,
  Mail,
  Phone,
  CalendarDays,
  CreditCard,
  Receipt,
  DollarSign,
  Loader2,
  ArrowRight,
  AlertCircle
} from "lucide-react";
import { getAppointmentDetails } from "../../api/api";

const AppointmentDetails = ({ appointmentToken }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appointmentData, setAppointmentData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);

  console.log("Received appointmentToken:", appointmentToken);

  useEffect(() => {
    if (appointmentToken) {
      fetchAppointmentDetails();
    } else {
      setError("No appointment token provided");
      setLoading(false);
    }
  }, [appointmentToken]);

  const fetchAppointmentDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAppointmentDetails(appointmentToken);
      console.log("Appointment Details Response:", response);

      if (response && response.status === true) {
        const data = response.data?.[0] || {};
        const paymentDetails = response.appointmentPaymentDetails?.[0] || {};
        
        setAppointmentData(data);
        setPaymentData(paymentDetails);
      } else {
        setError(response?.message || "Failed to fetch appointment details");
      }
    } catch (err) {
      console.error("Error fetching appointment details:", err);
      setError(err.message || "Failed to fetch appointment details");
    } finally {
      setLoading(false);
    }
  };

  // If loading, show loading state
  if (loading) {
    return (
      <div className="w-full min-h-[60vh] bg-[#F8F7F3] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="text-green-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading appointment details...</p>
        </div>
      </div>
    );
  }

  // If error, show error state
  if (error || !appointmentData) {
    return (
      <div className="w-full min-h-[60vh] bg-[#F8F7F3] flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-sm max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-gray-700">Failed to load details</h3>
          <p className="text-gray-500 mt-2">{error || "Appointment not found"}</p>
        </div>
      </div>
    );
  }

  const appointment = appointmentData;
  const payment = paymentData;

  // Get status color
  const getStatusColor = (status) => {
    if (status === 0) return "bg-yellow-100 text-yellow-700";
    if (status === 1) return "bg-blue-100 text-blue-700";
    if (status === 2) return "bg-green-100 text-green-700";
    if (status === 3) return "bg-red-100 text-red-700";
    return "bg-gray-100 text-gray-700";
  };

  const getStatusText = (status) => {
    if (status === 0) return "Upcoming";
    if (status === 1) return "In Progress";
    if (status === 2) return "Completed";
    if (status === 3) return "Cancelled";
    return "Unknown";
  };

  const getPaymentStatusColor = (status) => {
    if (status === "success") return "bg-green-100 text-green-700";
    if (status === "pending") return "bg-yellow-100 text-yellow-700";
    if (status === "failed") return "bg-red-100 text-red-700";
    return "bg-gray-100 text-gray-700";
  };

  const getPaymentStatusText = (status) => {
    if (status === "success") return "Payment Successful";
    if (status === "pending") return "Payment Pending";
    if (status === "failed") return "Payment Failed";
    return "Unknown";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return `₹${(amount / 100).toFixed(2)}`;
  };

  // Check if video call is enabled
  const isCallEnabled = () => {
    const mode = appointment.mode_consultation?.toLowerCase() || "";
    if (mode.includes("video")) {
      return appointment.video_link && appointment.video_link.trim() !== "";
    } else if (mode.includes("voice") || mode.includes("audio")) {
      return appointment.phone_number && appointment.phone_number.trim() !== "";
    }
    return false;
  };

  const callEnabled = isCallEnabled();

  return (
    <div className="w-full bg-[#F8F7F3] font-lato">
      <div className="w-full">
        {/* Main Content */}
        <div className="w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* LEFT COLUMN */}
            <div className="col-span-1 lg:col-span-7">
              {/* Patient Card */}
              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 sm:p-7">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                  <div className="flex gap-4 sm:gap-5">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-100 flex items-center justify-center">
                      <User size={32} className="text-green-600" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <h2 className="text-xl sm:text-3xl font-bold text-gray-800">
                          {appointment.name || "Patient"}
                        </h2>
                        <span className={`${getStatusColor(appointment.status)} text-xs font-semibold px-3 py-1 rounded-full`}>
                          {getStatusText(appointment.status)}
                        </span>
                        <span className="bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full">
                          {appointment.mode_consultation || "Consultation"}
                        </span>
                      </div>
                      <p className="text-gray-500 mt-1 sm:mt-2">
                        {appointment.age || "N/A"} Years • {appointment.gender || "N/A"}
                      </p>
                      <div className="flex items-center gap-2 mt-2 sm:mt-3">
                        <Mail size={14} className="text-gray-400" />
                        <span className="text-sm text-gray-600">{appointment.email || "N/A"}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Phone size={14} className="text-gray-400" />
                        <span className="text-sm text-gray-600">{appointment.phone || "N/A"}</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-left sm:text-right">
                    <p className="text-xs uppercase text-gray-400">Appointment ID</p>
                    <div className="mt-2 inline-block rounded-full bg-gray-100 px-4 py-2 font-semibold text-sm">
                      #{appointment.id || "N/A"}
                    </div>
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mt-6 sm:mt-8">
                  <div className="bg-gray-50 rounded-2xl p-4 sm:p-5">
                    <p className="text-xs uppercase text-gray-400">Purpose</p>
                    <h4 className="mt-2 font-bold text-gray-800 text-sm sm:text-base">
                      {appointment.purpose || "Consultation"}
                    </h4>
                  </div>
                  <div className="bg-gray-50 rounded-2xl p-4 sm:p-5">
                    <p className="text-xs uppercase text-gray-400">Type</p>
                    <h4 className="mt-2 font-bold text-gray-800 text-sm sm:text-base">
                      {appointment.type || "General"}
                    </h4>
                  </div>
                  <div className="bg-gray-50 rounded-2xl p-4 sm:p-5">
                    <p className="text-xs uppercase text-gray-400">Follow-up Status</p>
                    <h4 className="mt-2 font-bold text-gray-800 text-sm sm:text-base capitalize">
                      {appointment.followup_status || "N/A"}
                    </h4>
                  </div>
                </div>

                {/* Action Buttons - Disabled like MyAppointments */}
                <div className="flex flex-wrap gap-3 sm:gap-4 mt-6 sm:mt-8">
                  {appointment.status === 0 && (
                    <>
                      <button
                        disabled={!callEnabled}
                        className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium transition text-sm sm:text-base ${
                          callEnabled
                            ? "bg-green-600 hover:bg-green-700 text-white"
                            : "bg-[#eef1ef] text-[#aaa] cursor-not-allowed"
                        }`}
                      >
                        <Video size={18} />
                        Join Video Call
                      </button>
                      <button
                        disabled={true}
                        className="flex items-center gap-2 border border-[#e5e9e5] text-[#b0b7b1] text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium bg-[#f5f7f5] cursor-not-allowed transition"
                      >
                        <RotateCcw size={18} />
                        Reschedule
                      </button>
                      <button
                        disabled={true}
                        className="flex items-center gap-2 border border-[#e5e9e5] text-[#b0b7b1] text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium bg-[#f5f7f5] cursor-not-allowed transition"
                      >
                        <XCircle size={18} />
                        Cancel Appointment
                      </button>
                    </>
                  )}
                </div>
              </div>

            </div>

            {/* RIGHT COLUMN */}
            <div className="col-span-1 lg:col-span-5 flex flex-col gap-6">
              {/* Session Details */}
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 sm:p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Session Details</h2>

                <div className="space-y-6 sm:space-y-7">
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-green-500 mt-1"></div>
                      <div className="w-px flex-1 bg-gray-200 h-8"></div>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400">Date</p>
                      <p className="font-semibold text-gray-800 mt-1">
                        <CalendarDays size={14} className="inline mr-2 text-green-500" />
                        {formatDate(appointment.appointment_date)}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-green-500 mt-1"></div>
                      <div className="w-px flex-1 bg-gray-200 h-8"></div>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400">Time</p>
                      <p className="font-semibold text-gray-800 mt-1">
                        <Clock size={14} className="inline mr-2 text-green-500" />
                        {formatTime(appointment.appointment_time)}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-green-500 mt-1"></div>
                      <div className="w-px flex-1 bg-gray-200 h-8"></div>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400">Consultation Mode</p>
                      <p className="font-semibold text-gray-800 mt-1">
                        <Video size={14} className="inline mr-2 text-green-500" />
                        {appointment.mode_consultation || "Video Call"}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-3 h-3 rounded-full bg-green-500 mt-1"></div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400">Status</p>
                      <p className="font-semibold text-gray-800 mt-1">
                        <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(appointment.status)}`}>
                          {getStatusText(appointment.status)}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetails;